import React, { Fragment, useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import pluralize from 'pluralize';

import MarcomLayout from '../MarcomLayout';
import useAccountDelete from './hooks/useAccountDelete';
import useGlobalToast from '../../../hooks/useGlobalToast';
import useLogout from '../../auth/hooks/useLogout';
import getConfig from '../../../util/config';
import Button from '../Button';

const AccountDeleteView = () => {
  const config = getConfig();
  const deleteInterval = useRef();
  const logoutTimer = useRef();
  const [canDelete, setCanDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(5);
  const [loadingConfirmation, setLoadingConfirmation] = useState(false);
  const { setToast } = useGlobalToast();
  const { logout } = useLogout();
  const { onAccountDelete, deleting } = useAccountDelete({
    onSuccess: () => {
      setDeleted(true);
      setToast(
        "Your account has been deleted. You will be redirected to Waldo's website shortly",
      );
      logoutTimer.current = setTimeout(async () => {
        await logout({
          redirectUrl: config.marcomUrl,
        });
      }, 2500);
    },
  });

  useEffect(() => {
    return () => {
      if (deleteInterval?.current) {
        clearInterval(deleteInterval.current);
      }

      if (logoutTimer?.current) {
        clearTimeout(logoutTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (remainingSeconds === 0) {
      setCanDelete(true);
      setLoadingConfirmation(false);
    }
  }, [remainingSeconds]);

  const actionCopy = (() => {
    if (deleted) {
      return 'Account deleted';
    }

    if (loadingConfirmation || canDelete) {
      return 'Yes, delete my account';
    }

    return 'Delete account';
  })();

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Delete Account</title>
      </Helmet>
      <MarcomLayout.Wrapper title="Delete account" signupDisabled>
        {(loadingConfirmation || canDelete) && (
          <p className="text-center">
            This is an irreversible action. Are you sure you want to do this?
            {remainingSeconds > 0 && (
              <Fragment>
                <br />
                <br />
                <b>
                  The button will be enabled in{' '}
                  {pluralize('second', remainingSeconds, true)}...
                </b>
              </Fragment>
            )}
          </p>
        )}
        {!loadingConfirmation && !canDelete && (
          <p className="text-center">
            You are about to delete your account. After deletion, your events,
            circles, subscription &amp; information will be inaccessible.
            <br />
            <br />
            Do you want to proceed?
          </p>
        )}

        <div className="text-center mt-2">
          <Button
            btnStyle="danger"
            disabled={deleted || deleting || loadingConfirmation}
            onClick={() => {
              if (canDelete) {
                onAccountDelete();
                return;
              }
              if (remainingSeconds > 0) {
                setLoadingConfirmation(true);

                deleteInterval.current = setInterval(() => {
                  setRemainingSeconds(prev => prev - 1);
                }, 1000);
              }
            }}
          >
            {deleting ? 'Deleting...' : `${actionCopy}`}
          </Button>
        </div>
      </MarcomLayout.Wrapper>
    </Fragment>
  );
};

export default AccountDeleteView;
